import React, { useState, useEffect, useCallback } from 'react';
import { CheckCircle, XCircle, AlertTriangle, ChevronDown, ChevronUp, BookOpen, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';

const API_BASE = 'https://app.pvslimited.co.uk';

const getAuthToken = () => localStorage.getItem('token');

const KnowledgeReviewPanel = () => {
  const [facts, setFacts] = useState([]);
  const [flaggedFacts, setFlaggedFacts] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('pending');
  const [expandedFact, setExpandedFact] = useState(null);
  const [editingAnswer, setEditingAnswer] = useState({});
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [selectedIds, setSelectedIds] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [filterDoc, setFilterDoc] = useState('all');

  const fetchData = useCallback(async () => {
    setLoading(true);
    const token = getAuthToken();
    if (!token) return;

    const headers = { 'Authorization': `Bearer ${token}` };

    try {
      const [pendingRes, flaggedRes, statsRes] = await Promise.all([
        fetch(`${API_BASE}/api/vesselmind/admin/knowledge/pending?page=${page}&limit=20`, { headers }),
        fetch(`${API_BASE}/api/vesselmind/admin/knowledge/flagged`, { headers }),
        fetch(`${API_BASE}/api/vesselmind/admin/knowledge/stats`, { headers })
      ]);

      if (pendingRes.ok) {
        const data = await pendingRes.json();
        setFacts(data.facts || []);
        setTotalPages(data.totalPages || 1);
      }
      if (flaggedRes.ok) {
        const data = await flaggedRes.json();
        setFlaggedFacts(data.facts || []);
      }
      if (statsRes.ok) {
        const data = await statsRes.json();
        setStats(data.stats || null);
      }
    } catch (err) {
      console.error('Failed to fetch knowledge data:', err);
    }
    setLoading(false);
  }, [page]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleVerify = async (factId) => {
    const token = getAuthToken();
    const answer = editingAnswer[factId];

    const res = await fetch(`${API_BASE}/api/vesselmind/admin/knowledge/${factId}/verify`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ answer, notes: '' })
    });

    if (res.ok) {
      setFacts(prev => prev.filter(f => f._id !== factId));
      setFlaggedFacts(prev => prev.filter(f => f._id !== factId));
      fetchData();
    }
  };

  const handleReject = async (factId, reason) => {
    const token = getAuthToken();
    const res = await fetch(`${API_BASE}/api/vesselmind/admin/knowledge/${factId}/reject`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ reason: reason || 'Incorrect or inaccurate' })
    });

    if (res.ok) {
      setFacts(prev => prev.filter(f => f._id !== factId));
      setFlaggedFacts(prev => prev.filter(f => f._id !== factId));
      fetchData();
    }
  };

  const handleBulkVerify = async () => {
    if (selectedIds.length === 0) return;
    const token = getAuthToken();

    const res = await fetch(`${API_BASE}/api/vesselmind/admin/knowledge/verify-bulk`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${token}` },
      body: JSON.stringify({ factIds: selectedIds })
    });

    if (res.ok) {
      setSelectedIds([]);
      fetchData();
    }
  };

  const handleRefreshCache = async () => {
    const token = getAuthToken();
    await fetch(`${API_BASE}/api/vesselmind/admin/knowledge/refresh-cache`, {
      method: 'POST',
      headers: { 'Authorization': `Bearer ${token}` }
    });
    fetchData();
  };

  const toggleSelect = (id) => {
    setSelectedIds(prev =>
      prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]
    );
  };

  const selectAll = () => {
    const currentFacts = activeTab === 'pending' ? facts : flaggedFacts;
    if (selectedIds.length === currentFacts.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(currentFacts.map(f => f._id));
    }
  };

  const rawFacts = activeTab === 'pending' ? facts : flaggedFacts;
  const currentFacts = rawFacts.filter(f => {
    if (filterType !== 'all' && f.factType !== filterType) return false;
    if (filterDoc !== 'all' && f.documentTitle !== filterDoc) return false;
    if (searchTerm) {
      const term = searchTerm.toLowerCase();
      const inFact = (f.extractedFact || '').toLowerCase().includes(term);
      const inAnswer = (f.verifiedAnswer || '').toLowerCase().includes(term);
      const inNotes = (f.adminNotes || '').toLowerCase().includes(term);
      if (!inFact && !inAnswer && !inNotes) return false;
    }
    return true;
  });
  const uniqueDocuments = [...new Set(rawFacts.map(f => f.documentTitle).filter(Boolean))].sort();
  const factTypes = [...new Set(rawFacts.map(f => f.factType).filter(Boolean))].sort();

  if (loading && !stats) {
    return (
      <div className="flex items-center justify-center p-8">
        <RefreshCw className="w-6 h-6 animate-spin text-pvs-accent" />
        <span className="ml-2 text-pvs-text-muted">Loading knowledge base...</span>
      </div>
    );
  }

  return (
    <div className="bg-pvs-card border border-pvs-accent/30 rounded-xl shadow-pvs-glow">
      {/* Stats Bar */}
      {stats && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4 p-4 bg-pvs-card-light border-b border-pvs-border rounded-t-xl">
          <StatCard label="Total Facts" value={stats.total} />
          <StatCard label="Verified" value={stats.verified} color="text-green-400" />
          <StatCard label="Pending" value={stats.pending} color="text-yellow-400" />
          <StatCard label="Flagged" value={stats.flagged} color="text-red-400" />
          <StatCard label="Q&A Pairs" value={stats.totalQAPairs} color="text-pvs-accent" />
        </div>
      )}

      {/* Tabs & Actions */}
      <div className="flex items-center justify-between border-b border-pvs-border px-4 py-3">
        <div className="flex gap-2">
          <TabButton
            active={activeTab === 'pending'}
            onClick={() => setActiveTab('pending')}
            count={facts.length}
          >
            Pending Review
          </TabButton>
          <TabButton
            active={activeTab === 'flagged'}
            onClick={() => setActiveTab('flagged')}
            count={flaggedFacts.length}
            variant="warning"
          >
            Flagged
          </TabButton>
        </div>

        <div className="flex gap-2">
          {selectedIds.length > 0 && (
            <button
              onClick={handleBulkVerify}
              className="flex items-center gap-1 px-3 py-1.5 bg-green-600 text-white text-sm rounded hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Verify {selectedIds.length} selected
            </button>
          )}
          <button
            onClick={handleRefreshCache}
            className="flex items-center gap-1 px-3 py-1.5 text-pvs-text-muted hover:text-pvs-accent text-sm rounded border border-pvs-border hover:bg-pvs-accent/10 transition-colors"
          >
            <RefreshCw className="w-4 h-4" />
            Refresh Cache
          </button>
        </div>
      </div>

      {/* Search & Filters */}
      <div className="px-4 py-3 bg-pvs-card-light border-b border-pvs-border flex flex-wrap gap-3 items-center">
        <input
          type="text"
          placeholder="Search facts, answers, notes..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="flex-1 min-w-[200px] px-3 py-1.5 text-sm bg-pvs-input-bg border border-pvs-input-border text-white rounded focus:outline-none focus:ring-1 focus:ring-pvs-accent/50 placeholder-pvs-text-dim"
        />
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-1.5 text-sm bg-pvs-input-bg border border-pvs-input-border text-white rounded focus:outline-none focus:ring-1 focus:ring-pvs-accent/50"
        >
          <option value="all">All Types</option>
          {factTypes.map(t => (
            <option key={t} value={t}>{t}</option>
          ))}
        </select>
        <select
          value={filterDoc}
          onChange={(e) => setFilterDoc(e.target.value)}
          className="px-3 py-1.5 text-sm bg-pvs-input-bg border border-pvs-input-border text-white rounded max-w-[250px] focus:outline-none focus:ring-1 focus:ring-pvs-accent/50"
        >
          <option value="all">All Documents</option>
          {uniqueDocuments.map(d => (
            <option key={d} value={d}>{d.length > 40 ? d.slice(0, 40) + '...' : d}</option>
          ))}
        </select>
        {(searchTerm || filterType !== 'all' || filterDoc !== 'all') && (
          <button
            onClick={() => { setSearchTerm(''); setFilterType('all'); setFilterDoc('all'); }}
            className="px-2 py-1.5 text-xs text-pvs-text-dim hover:text-pvs-text-muted"
          >
            Clear filters
          </button>
        )}
        <span className="text-xs text-pvs-text-dim">{currentFacts.length} results</span>
      </div>

      {/* Select All */}
      {currentFacts.length > 0 && (
        <div className="px-4 py-2 bg-pvs-card-light border-b border-pvs-border flex items-center gap-2">
          <input
            type="checkbox"
            checked={selectedIds.length === currentFacts.length && currentFacts.length > 0}
            onChange={selectAll}
            className="rounded"
          />
          <span className="text-sm text-pvs-text-muted">
            {selectedIds.length > 0 ? `${selectedIds.length} selected` : 'Select all'}
          </span>
        </div>
      )}

      {/* Facts List */}
      <div className="divide-y divide-pvs-border max-h-[600px] overflow-y-auto">
        {currentFacts.length === 0 ? (
          <div className="p-8 text-center text-pvs-text-dim">
            {activeTab === 'pending'
              ? 'No facts pending review. Run the extraction script to populate.'
              : 'No flagged facts. All numbers verified successfully.'}
          </div>
        ) : (
          currentFacts.map(fact => (
            <FactCard
              key={fact._id}
              fact={fact}
              isExpanded={expandedFact === fact._id}
              isSelected={selectedIds.includes(fact._id)}
              onToggleExpand={() => setExpandedFact(expandedFact === fact._id ? null : fact._id)}
              onToggleSelect={() => toggleSelect(fact._id)}
              onVerify={() => handleVerify(fact._id)}
              onReject={(reason) => handleReject(fact._id, reason)}
              editingAnswer={editingAnswer[fact._id] || fact.verifiedAnswer || ''}
              onAnswerChange={(val) => setEditingAnswer(prev => ({ ...prev, [fact._id]: val }))}
            />
          ))
        )}
      </div>

      {/* Pagination */}
      {activeTab === 'pending' && totalPages > 1 && (
        <div className="flex items-center justify-center gap-4 p-4 border-t border-pvs-border">
          <button
            onClick={() => setPage(p => Math.max(1, p - 1))}
            disabled={page <= 1}
            className="p-1 rounded hover:bg-pvs-card-light disabled:opacity-30"
          >
            <ChevronLeft className="w-5 h-5" />
          </button>
          <span className="text-sm text-pvs-text-muted">Page {page} of {totalPages}</span>
          <button
            onClick={() => setPage(p => Math.min(totalPages, p + 1))}
            disabled={page >= totalPages}
            className="p-1 rounded hover:bg-pvs-card-light disabled:opacity-30"
          >
            <ChevronRight className="w-5 h-5" />
          </button>
        </div>
      )}
    </div>
  );
};

const StatCard = ({ label, value, color = 'text-white' }) => (
  <div className="text-center">
    <div className={`text-2xl font-bold ${color}`}>{value || 0}</div>
    <div className="text-xs text-pvs-text-muted">{label}</div>
  </div>
);

const TabButton = ({ active, onClick, count, variant, children }) => (
  <button
    onClick={onClick}
    className={`flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-lg transition-colors ${
      active
        ? variant === 'warning'
          ? 'bg-amber-900/20 text-amber-400'
          : 'bg-pvs-accent/10 text-pvs-accent'
        : 'text-pvs-text-dim hover:text-pvs-text-muted hover:bg-pvs-card-light'
    }`}
  >
    {variant === 'warning' && <AlertTriangle className="w-4 h-4" />}
    {children}
    {count > 0 && (
      <span className={`px-2 py-0.5 text-xs rounded-full ${
        active ? 'bg-white/10' : 'bg-pvs-card-light'
      }`}>
        {count}
      </span>
    )}
  </button>
);

const FactCard = ({
  fact, isExpanded, isSelected,
  onToggleExpand, onToggleSelect,
  onVerify, onReject,
  editingAnswer, onAnswerChange
}) => {
  const [rejectReason, setRejectReason] = useState('');
  const [showReject, setShowReject] = useState(false);

  const numCheck = fact.numberVerification || {};

  return (
    <div className={`p-4 ${isSelected ? 'bg-pvs-accent/10' : 'hover:bg-pvs-card-light'} transition-colors`}>
      {/* Header row */}
      <div className="flex items-start gap-3">
        <input
          type="checkbox"
          checked={isSelected}
          onChange={onToggleSelect}
          className="mt-1 rounded"
        />

        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 mb-1">
            <span className={`px-2 py-0.5 text-xs rounded-full font-medium ${
              fact.factType === 'safety' ? 'bg-red-900/30 text-red-400' :
              fact.factType === 'rule' ? 'bg-pvs-accent/10 text-pvs-accent' :
              fact.factType === 'measurement' ? 'bg-green-900/30 text-pvs-accent' :
              fact.factType === 'table_value' ? 'bg-purple-900/30 text-purple-400' :
              'bg-pvs-card-light text-pvs-text-muted'
            }`}>
              {fact.factType}
            </span>
            <span className="text-xs text-pvs-text-dim">{fact.documentTitle}</span>
            {fact.sectionNumber && (
              <span className="text-xs text-pvs-text-dim">Section {fact.sectionNumber}</span>
            )}

            {/* Number verification badge */}
            {numCheck.passed === true && (
              <span className="flex items-center gap-1 text-xs text-pvs-accent">
                <CheckCircle className="w-3 h-3" /> Numbers verified
              </span>
            )}
            {numCheck.passed === false && (
              <span className="flex items-center gap-1 text-xs text-red-400">
                <AlertTriangle className="w-3 h-3" /> Number mismatch: {(numCheck.numbersMissing || []).join(', ')}
              </span>
            )}
          </div>

          <p className="text-sm text-white font-medium">{fact.extractedFact}</p>

          {fact.adminNotes && (
            <div className={`mt-2 px-3 py-1.5 rounded text-xs ${
              fact.adminNotes.includes('REJECTED') ? 'bg-red-900/30 text-red-400 border border-red-500/30' :
              fact.adminNotes.includes('Auto-approved') ? 'bg-green-900/30 text-pvs-accent border border-green-500/30' :
              'bg-amber-900/20 text-amber-400 border border-amber-500/30'
            }`}>
              <span className="font-medium">Auto-review: </span>{fact.adminNotes}
            </div>
          )}

          {fact.qaPairs && fact.qaPairs.length > 0 && (
            <p className="text-xs text-pvs-text-dim mt-1">
              {fact.qaPairs.length} question variations generated
            </p>
          )}
        </div>

        <button
          onClick={onToggleExpand}
          className="p-1 text-pvs-text-dim hover:text-pvs-text-muted"
        >
          {isExpanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
        </button>
      </div>

      {/* Expanded details */}
      {isExpanded && (
        <div className="mt-4 ml-8 space-y-4">
          {/* Source Quote */}
          <div className="p-3 bg-amber-900/20 border-l-4 border-amber-400 rounded">
            <p className="text-xs font-semibold text-amber-400 mb-1">
              <BookOpen className="w-3 h-3 inline mr-1" />
              Source Quote (from PDF):
            </p>
            <p className="text-sm text-pvs-text-muted italic whitespace-pre-wrap">
              "{fact.sourceQuote?.exactQuote || 'No source quote available'}"
            </p>
          </div>

          {/* Conditions */}
          {fact.conditions && fact.conditions.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-pvs-text-muted mb-1">Conditions:</p>
              <div className="flex flex-wrap gap-1">
                {fact.conditions.map((c, i) => (
                  <span key={i} className="px-2 py-0.5 bg-pvs-card-light text-pvs-text-muted text-xs rounded">
                    {c}
                  </span>
                ))}
              </div>
            </div>
          )}

          {/* Generated Questions */}
          {fact.qaPairs && fact.qaPairs.length > 0 && (
            <div>
              <p className="text-xs font-semibold text-pvs-text-muted mb-1">Question Variations:</p>
              <ul className="space-y-1">
                {fact.qaPairs.map((qa, i) => (
                  <li key={i} className="text-sm text-pvs-text-muted pl-3 border-l-2 border-pvs-border">
                    {qa.question}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Editable Answer */}
          <div>
            <p className="text-xs font-semibold text-pvs-text-muted mb-1">
              Verified Answer (edit if needed):
            </p>
            <textarea
              value={editingAnswer}
              onChange={(e) => onAnswerChange(e.target.value)}
              rows={4}
              className="w-full p-3 bg-pvs-input-bg border border-pvs-input-border text-white rounded-lg text-sm focus:ring-2 focus:ring-pvs-accent/50 focus:border-pvs-accent/50 focus:outline-none placeholder-pvs-text-dim"
              placeholder="The answer that will be shown to users..."
            />
          </div>

          {/* Actions */}
          <div className="flex items-center gap-3">
            <button
              onClick={onVerify}
              className="flex items-center gap-1 px-4 py-2 bg-green-600 text-white text-sm rounded-lg hover:bg-green-700 transition-colors"
            >
              <CheckCircle className="w-4 h-4" />
              Verify & Publish
            </button>

            {!showReject ? (
              <button
                onClick={() => setShowReject(true)}
                className="flex items-center gap-1 px-4 py-2 text-red-400 text-sm rounded-lg border border-red-500/30 hover:bg-red-900/30 transition-colors"
              >
                <XCircle className="w-4 h-4" />
                Reject
              </button>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="text"
                  value={rejectReason}
                  onChange={(e) => setRejectReason(e.target.value)}
                  placeholder="Reason for rejection..."
                  className="px-3 py-2 bg-pvs-input-bg border border-pvs-input-border text-white rounded text-sm focus:ring-2 focus:ring-red-500/30 focus:outline-none placeholder-pvs-text-dim"
                />
                <button
                  onClick={() => { onReject(rejectReason); setShowReject(false); }}
                  className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700"
                >
                  Confirm
                </button>
                <button
                  onClick={() => setShowReject(false)}
                  className="px-3 py-2 text-pvs-text-dim text-sm"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default KnowledgeReviewPanel;
